import React, { useState, useEffect, useMemo } from "react";
import {
  View,
  ActivityIndicator,
  TextInput,
  TouchableOpacity,
  Picker,
} from "react-native";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Skeleton,
} from "@mui/material";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { debounce } from "lodash";
import { fetchAsignacionLog } from "../../services/ConnectDataBase.js";
import { styles } from "../../style/FoundryStyle.js";
import { BACKGROUND_COLORS, BORDER_COLORS, TEXT_COLORS, LOADING_COLORS } from "../../style/colors.js";

// Configuración de skeletons de carga
const SKELETON_CONFIG = {
  rows: 10,             // Número de filas de skeletons
  height: 20,           // Altura de los skeletons en px
  widthLabel: "100%",    // Ancho del skeleton de etiqueta
  widthValue: "100%",    // Ancho del skeleton de valor
};

const ComponentAsignaciones = () => {
  const [asignaciones, setAsignaciones] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchDate, setSearchDate] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [searchColumn, setSearchColumn] = useState("recibe");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await fetchAsignacionLog();

        data.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        setAsignaciones(data);
        setFilteredData(data);
        setIsLoading(false);
      } catch (error) {
        console.error("Error al cargar las asignaciones:", error);
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const applyFilters = useMemo(
    () =>
      debounce(() => {
        let filtered = asignaciones;

        if (searchTerm.trim()) {
          const lowerSearchTerm = searchTerm.toLowerCase();
          filtered = filtered.filter((item) =>
            item[searchColumn].toLowerCase().includes(lowerSearchTerm)
          );
        }

        if (searchDate.trim()) {
          filtered = filtered.filter((item) =>
            item.timestamp.startsWith(searchDate.trim())
          );
        }

        setFilteredData(filtered);
        setPage(0);
      }, 300),
    [asignaciones, searchColumn, searchTerm, searchDate]
  );

  useEffect(() => {
    applyFilters();
    return () => applyFilters.cancel();
  }, [searchTerm, searchDate, applyFilters]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <View style={styles.containerRepairScreen}>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          marginBottom: 15,
          paddingHorizontal: 10,
        }}>
        <TextInput
          style={{
            flex: 1,
            backgroundColor: BACKGROUND_COLORS.CARD,
            borderColor: BORDER_COLORS.LIGHT,
            borderWidth: 1,
            paddingHorizontal: 10,
            paddingVertical: 8,
            fontSize: 16,
            borderRadius: 8,
          }}
          placeholder={`Buscar por ${searchColumn}`}
          placeholderTextColor={TEXT_COLORS.SECONDARY}
          value={searchTerm}
          onChangeText={setSearchTerm}
        />
        <TouchableOpacity
          style={{
            backgroundColor: BACKGROUND_COLORS.PRIMARY,
            borderRadius: 8,
            padding: 8,
            marginLeft: 10,
          }}
          onPress={applyFilters}>
          <MaterialCommunityIcons name='magnify' size={20} color={TEXT_COLORS.LIGHT} />
        </TouchableOpacity>

        <Picker
          selectedValue={searchColumn}
          onValueChange={(value) => setSearchColumn(value)}
          style={{
            width: 150,
            marginLeft: 10,
            backgroundColor: BACKGROUND_COLORS.CARD,
            borderColor: BORDER_COLORS.LIGHT,
            borderWidth: 1,
          }}>
          <Picker.Item label='Recibe' value='recibe' />
          <Picker.Item label='Entrega' value='entrega' />
          <Picker.Item label='MO' value='mo' />
        </Picker>
      </View>

      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          marginBottom: 15,
          paddingHorizontal: 10,
        }}>
        <TextInput
          style={{
            flex: 1,
            backgroundColor: BACKGROUND_COLORS.CARD,
            borderColor: BORDER_COLORS.LIGHT,
            borderWidth: 1,
            paddingHorizontal: 10,
            paddingVertical: 8,
            fontSize: 16,
            borderRadius: 8,
          }}
          placeholder='Buscar por fecha (YYYY o YYYY-MM o YYYY-MM-DD)'
          placeholderTextColor={TEXT_COLORS.SECONDARY}
          value={searchDate}
          onChangeText={setSearchDate}
        />
      </View>

      {isLoading ? (
        <>
          <TableContainer
            component={Paper}
            style={{
              ...styles.tableContainerRepairScreen,
              maxHeight: "65vh",
            }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    "FECHA ENTREGA",
                    "ENTREGA",
                    "RECIBE",
                    "MO",
                    "FECHA RECIBE",
                  ].map((header, i) => (
                    <TableCell
                      key={i}
                      style={{
                        ...styles.headerCellRepairScreen,
                        fontFamily: "monospace",
                      }}>
                      <Skeleton variant="text" width="80%" height={20} sx={{ mx: "auto" }} />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {[...Array(SKELETON_CONFIG.rows)].map((_, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {[...Array(5)].map((_, colIndex) => (
                      <TableCell
                        key={colIndex}
                        style={{
                          ...styles.cellRepairScreen,
                          fontFamily: "monospace",
                        }}>
                        <Skeleton variant="text" width={SKELETON_CONFIG.widthValue} height={SKELETON_CONFIG.height} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <>
          <TableContainer
            component={Paper}
            style={{
              ...styles.tableContainerRepairScreen,
              maxHeight: "65vh",
            }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    style={{
                      ...styles.headerCellRepairScreen,
                      fontFamily: "monospace",
                    }}>
                    FECHA ENTREGA
                  </TableCell>
                  <TableCell
                    style={{
                      ...styles.headerCellRepairScreen,
                      fontFamily: "monospace",
                    }}>
                    ENTREGA
                  </TableCell>
                  <TableCell
                    style={{
                      ...styles.headerCellRepairScreen,
                      fontFamily: "monospace",
                    }}>
                    RECIBE
                  </TableCell>
                  <TableCell
                    style={{
                      ...styles.headerCellRepairScreen,
                      fontFamily: "monospace",
                    }}>
                    MO
                  </TableCell>
                  <TableCell
                    style={{
                      ...styles.headerCellRepairScreen,
                      fontFamily: "monospace",
                    }}>
                    FECHA RECIBE
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item, index) => (
                    <TableRow key={index}>
                      <TableCell
                        style={{
                          ...styles.cellRepairScreen,
                          fontFamily: "monospace",
                        }}>
                        {item.timestamp.toUpperCase()}
                      </TableCell>
                      <TableCell
                        style={{
                          ...styles.cellRepairScreen,
                          fontFamily: "monospace",
                        }}>
                        {item.entrega.toUpperCase()}
                      </TableCell>
                      <TableCell
                        style={{
                          ...styles.cellRepairScreen,
                          fontFamily: "monospace",
                        }}>
                        {item.recibe.toUpperCase()}
                      </TableCell>
                      <TableCell
                        style={{
                          ...styles.cellRepairScreen,
                          fontFamily: "monospace",
                        }}>
                        {item.mo.toUpperCase()}
                      </TableCell>
                      <TableCell
                        style={{
                          ...styles.cellRepairScreen,
                          fontFamily: "monospace",
                        }}>
                        {item.fechaHora.toUpperCase()}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 100, 200]}
            component='div'
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}
    </View>
  );
};

export default ComponentAsignaciones;
